/**
 * StatusBadge (Nano-Metadata Table)
 *
 * ✅ ENTERPRISE V2: Strictly Deterministic Status Display
 *
 * RULES:
 *   1. Status derived ONLY from row.status (set by useNanoMetadataTableSync)
 *   2. NO independent logic - single source of truth
 *   3. NO engineering jargon shown to users
 */

import React from 'react';

import { CheckCircle, Loader2, AlertTriangle, XCircle, Clock, Copy } from 'lucide-react';
import '@/components/smart-notebook/SmartTable/SmartTable.css';

export default function StatusBadge({ row }) {
  const status = row?.status || 'processing';
  const isLoading = row?.isLoading === true;
  const processingStage = row?.user_display?.stage || row?.processingStage || '';

  // ═══════════════════════════════════════════════════════════════════════════
  // DETERMINISTIC STATUS DISPLAY (from row.status ONLY)
  // ═══════════════════════════════════════════════════════════════════════════

  // LOADING STATE (uploading or processing)
  if (isLoading || status === 'uploading' || status === 'processing') {
    return (
      <span
        className="badge badge-blue badge-animated"
        title={processingStage || 'Processing document...'}
      >
        <Loader2 className="badge-icon spinning" size={12} />
        {status === 'uploading' ? 'Uploading' : 'Processing'}
      </span>
    );
  }

  // ERROR STATE
  if (status === 'error' || status === 'failed') {
    const errorMsg = row?.statusReason || row?.error || 'Processing failed';
    return (
      <span className="badge badge-red" title={errorMsg}>
        <XCircle className="badge-icon" size={12} />
        Failed
      </span>
    );
  }

  // DUPLICATE STATE
  if (status === 'duplicate') {
    return (
      <span className="badge badge-gray" title="This file already exists in the notebook">
        <Copy className="badge-icon" size={12} />
        Duplicate
      </span>
    );
  }

  // QUEUED STATE
  if (status === 'queued') {
    return (
      <span className="badge badge-yellow" title="Waiting in queue...">
        <Clock className="badge-icon" size={12} />
        Queued
      </span>
    );
  }

  // READY STATE (ONLY when row.status === 'ready')
  if (status === 'ready') {
    // Sub-states based on health
    const healthScore = row?.quick_health?.ai_readiness_score;
    const healthTier = row?.quick_health?.health_tier || 'good';

    if (healthScore !== null && healthScore !== undefined) {
      if (healthTier === 'excellent' || healthScore >= 95) {
        return (
          <span className="badge badge-green" title="Fully indexed and ready for AI analysis">
            <CheckCircle className="badge-icon" size={12} />
            Ready
          </span>
        );
      }
      if (healthTier === 'good' || healthScore >= 85) {
        return (
          <span className="badge badge-green-soft" title="Ready with minor limitations">
            <CheckCircle className="badge-icon" size={12} />
            Ready
          </span>
        );
      }
      if (healthTier === 'fair' || healthScore >= 70) {
        return (
          <span className="badge badge-yellow" title="Ready but quality may be limited">
            <AlertTriangle className="badge-icon" size={12} />
            Ready
          </span>
        );
      }
      // Poor quality
      return (
        <span
          className="badge badge-orange"
          title="Ready but quality is low - consider re-uploading"
        >
          <AlertTriangle className="badge-icon" size={12} />
          Ready (Low Quality)
        </span>
      );
    }

    // Ready but health pending
    return (
      <span className="badge badge-green" title="Ready for AI analysis">
        <CheckCircle className="badge-icon" size={12} />
        Ready
      </span>
    );
  }

  // FALLBACK: Unknown status → treat as processing
  return (
    <span className="badge badge-blue badge-animated" title="Processing...">
      <Loader2 className="badge-icon spinning" size={12} />
      Processing
    </span>
  );
}
